import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FleetWidgetType } from 'models/enums/fleet-management';
import FleetWidget from 'models/fleet-management/FleetWidget';
import TableWidget from './TableWidget';
import { useAppSelector } from 'store';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextWidget from './TextWidget';
import CardWidget from './CardWidget';
import BarChartWidget from './BarChartWidget';
import Box from '@mui/material/Box';
import PieChartWidget from './PieChartWidget';
import {
  selectFleetData,
  selectFleetDataSetsData,
  selectFleetFieldsConfiguration,
} from 'store/fleet-management/fleet-management-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import LineChartWidget from './LineChartWidget';
import CumulativeChartWidget from './CumulativeChartWidget';
import { filterData, FleetFieldTypeLookup } from 'utils/fleet-filters';

const WidgetContainer: React.FC<{
  widget: FleetWidget;
  fullHeight?: boolean;
  unitWidth: number;
  isFormOpen: boolean;
  selectedWidget: { selectedId: string | null; onChange: (id: string | null) => void };
}> = ({ widget, fullHeight, unitWidth, isFormOpen, selectedWidget }) => {
  const liveData = useAppSelector((state) => selectFleetData(state)).find(
    (l) => l.id === widget.dataset,
  );
  const getStatus = liveData?.rest.getStatus ?? HttpFetchStatus.Idle;

  const datasets = useAppSelector(selectFleetDataSetsData);
  const fieldsConfiguration = useAppSelector(selectFleetFieldsConfiguration);

  const fieldTypes = useMemo<FleetFieldTypeLookup>(() => {
    const datasetModel = datasets?.find((d) => d.id === widget.dataset)?.model;

    if (datasetModel == null) {
      return () => undefined;
    }

    const typesByName = new Map(
      (fieldsConfiguration ?? [])
        .filter((config) => config.model === datasetModel)
        .map((config) => [config.fieldName, config.type]),
    );

    return (fieldName: string) => typesByName.get(fieldName);
  }, [datasets, fieldsConfiguration, widget.dataset]);

  const positioning = useMemo(() => {
    if (
      fullHeight ||
      widget.grid == null ||
      widget.grid.length !== 2 ||
      widget.grid.some((p) => p.length !== 2)
    ) {
      if (isFormOpen || !fullHeight) {
        return {
          position: 'absolute',
          left: 0,
          top: 0,
          width: unitWidth * 12,
          height: unitWidth * 12,
        };
      } else {
        return {
          height: '100%',
        };
      }
    }
    return {
      position: 'absolute',
      left: widget.grid[0][0] * unitWidth,
      top: widget.grid[0][1] * unitWidth,
      width: (widget.grid[1][0] - widget.grid[0][0]) * unitWidth,
      height: (widget.grid[1][1] - widget.grid[0][1]) * unitWidth,
    };
  }, [fullHeight, isFormOpen, unitWidth, widget.grid]);

  // Charts need pixels, and a full-height widget has none to derive: its
  // positioning is a bare `height: 100%`. Measuring the slot the chart
  // actually gets covers both layouts and drops the padding arithmetic.
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = chartAreaRef.current;
    if (element == null) return;

    const measure = () =>
      setChartSize({ width: element.clientWidth, height: element.clientHeight });

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    measure();

    return () => observer.disconnect();
    // The slot only exists for chart widgets, so a type change remounts it.
  }, [widget.type]);

  const selectWidgetClickHandler = () => {
    selectedWidget.onChange(widget.id);
  };

  const data = useMemo(
    () => filterData(liveData?.data ?? [], widget.filters),
    [liveData?.data, widget.filters],
  );

  return (
    <React.Fragment>
      <Box
        sx={{
          ...positioning,
          p: 1,
        }}
      >
        {widget.type === FleetWidgetType.TEXT ? (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              alignItems: 'center',
              display: 'flex',
              ':hover': isFormOpen ? { backgroundColor: 'grey' } : undefined,
            }}
          >
            <TextWidget widget={widget} unitWidth={unitWidth} />
          </Box>
        ) : (
          <Stack
            sx={(theme) => ({
              backgroundColor: theme.palette.background.paper,
              p: 2,
              height: '100%',
              width: '100%',
              borderRadius: theme.spacing(1),
            })}
            flex={fullHeight ? 1 : undefined}
            spacing={2}
          >
            <Stack>
              <Typography variant="h2">{widget.name}</Typography>
              {widget.description != null && (
                <Typography variant="subtitle2">{widget.description}</Typography>
              )}
            </Stack>
            {widget.type === FleetWidgetType.TABLE ? (
              <TableWidget
                fields={widget.fields?.flatMap((f) => f.name) ?? []}
                data={data}
                getStatus={getStatus}
              />
            ) : widget.type === FleetWidgetType.CARD ? (
              <CardWidget widget={widget} data={data} />
            ) : (
              <Box ref={chartAreaRef} sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                {widget.type === FleetWidgetType.PIE_CHART ? (
                  <PieChartWidget
                    widget={widget}
                    data={data}
                    getStatus={getStatus}
                    width={chartSize.width}
                    height={chartSize.height}
                    fieldTypes={fieldTypes}
                  />
                ) : widget.type === FleetWidgetType.BAR_CHART ? (
                  <BarChartWidget
                    widget={widget}
                    data={data}
                    getStatus={getStatus}
                    fieldTypes={fieldTypes}
                  />
                ) : widget.type === FleetWidgetType.LINE_CHART ? (
                  <LineChartWidget
                    widget={widget}
                    data={data}
                    getStatus={getStatus}
                    width={chartSize.width}
                    height={chartSize.height}
                    fieldTypes={fieldTypes}
                  />
                ) : widget.type === FleetWidgetType.CUMULATIVE_CHART ? (
                  <CumulativeChartWidget
                    widget={widget}
                    data={data}
                    getStatus={getStatus}
                    width={chartSize.width}
                    height={chartSize.height}
                  />
                ) : null}
              </Box>
            )}
          </Stack>
        )}
      </Box>
      {isFormOpen && (
        <Box
          onClick={selectWidgetClickHandler}
          sx={(theme) => ({
            position: 'absolute',
            top: (positioning.top ?? 0) + 8,
            left: (positioning.left ?? 0) + 8,
            width: (positioning.width ?? 0) - 16,
            height: (typeof positioning.height === 'string' ? 16 : (positioning.height ?? 0)) - 16,
            ':hover': {
              backgroundColor: theme.palette.action.hover,
              cursor: 'pointer',
              opacity: 0.2,
            },
            borderRadius: theme.spacing(1),
            boxShadow: selectedWidget.selectedId === widget.id ? 1 : undefined,
          })}
        />
      )}
    </React.Fragment>
  );
};

export default WidgetContainer;
